import { all, call, put, select, takeEvery, takeLatest } from "redux-saga/effects";
import { AxiosCall, AxiosFile, AxiosPatch, AxiosPost, AxiosDelete, AxiosDownload } from "../../../utils/axios";
import * as actions from "./actions";

// ==================== ARCHIVES ====================

function* fetchArchives(action) {
    try {
        const filters = action.payload || {};
        const queryParams = new URLSearchParams();

        Object.keys(filters).forEach(key => {
            if (filters[key] !== '' && filters[key] !== null && filters[key] !== undefined) {
                queryParams.append(key, filters[key]);
            }
        });

        const queryString = queryParams.toString();
        const url = `/mycore/archive/list/${queryString ? `?${queryString}` : ''}`;

        const result = yield call(AxiosCall, url);
        yield put(actions.fetchArchivesSuccess(result.data));
    } catch (e) {
        yield put(actions.fetchArchivesFailure(e.message));
    }
}

function* fetchArchiveDetail(action) {
    try {
        const { archiveId } = action.payload;
        const result = yield call(AxiosCall, `/mycore/${archiveId}/archive/detail/`);
        yield put(actions.fetchArchiveDetailSuccess(result.data));
    } catch (e) {
        yield put(actions.fetchArchiveDetailFailure(e.message));
    }
}

function* uploadArchive(action) {
    try {
        // Create FormData for file upload
        const formData = new FormData();
        const data = action.payload;

        // Append all fields to FormData
        Object.keys(data).forEach(key => {
            if (data[key] !== null && data[key] !== undefined) {
                if (key === 'tags' && Array.isArray(data[key])) {
                    formData.append(key, JSON.stringify(data[key]));
                } else {
                    formData.append(key, data[key]);
                }
            }
        });

        // Use AxiosFile for multipart/form-data uploads
        const result = yield call(AxiosFile, '/mycore/archive/upload/', formData);
        yield put(actions.uploadArchiveSuccess(result.data));
        // Refresh the list and storage stats
        yield put(actions.fetchArchivesRequest());
        if (data.organization) {
            yield put(actions.fetchStorageStatsRequest(data.organization));
        }
    } catch (e) {
        yield put(actions.uploadArchiveFailure(e.message || 'Error uploading file'));
    }
}

function* updateArchive(action) {
    try {
        const { archiveId, archiveData } = action.payload;
        const result = yield call(AxiosPatch, `/mycore/${archiveId}/archive/update/`, archiveData);
        yield put(actions.updateArchiveSuccess(result.data));
        // Optionally refresh the list
        yield put(actions.fetchArchivesRequest());
    } catch (e) {
        yield put(actions.updateArchiveFailure(e.message));
    }
}

function* deleteArchive(action) {
    try {
        const { archiveId } = action.payload;
        yield call(AxiosDelete, `/mycore/${archiveId}/archive/delete/`);
        yield put(actions.deleteArchiveSuccess(archiveId));
        // Refresh the list and storage stats
        yield put(actions.fetchArchivesRequest());
        // Get organization ID from current archives state to refresh stats
        const state = yield select(state => state.mycore.archives);
        if (state.filters.organization) {
            yield put(actions.fetchStorageStatsRequest(state.filters.organization));
        }
    } catch (e) {
        yield put(actions.deleteArchiveFailure(e.message));
    }
}

function* downloadArchive(action) {
    try {
        const { archiveId, fileName } = action.payload;

        // Make the API call to download the file
        const response = yield call(AxiosDownload, `/mycore/${archiveId}/archive/download/`);

        // Create a blob URL and trigger download
        const blob = new Blob([response.data], { type: response.headers['content-type'] });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName || 'download');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        yield put(actions.downloadArchiveSuccess());
    } catch (e) {
        yield put(actions.downloadArchiveFailure(e.message));
    }
}

// ==================== WATCHERS ====================

function* watchFetchArchives() {
    yield takeLatest(actions.FETCH_ARCHIVES.REQUEST, fetchArchives);
}

function* watchFetchArchiveDetail() {
    yield takeLatest(actions.FETCH_ARCHIVE_DETAIL.REQUEST, fetchArchiveDetail);
}

function* watchUploadArchive() {
    yield takeEvery(actions.UPLOAD_ARCHIVE.REQUEST, uploadArchive);
}

function* watchUpdateArchive() {
    yield takeEvery(actions.UPDATE_ARCHIVE.REQUEST, updateArchive);
}

function* watchDeleteArchive() {
    yield takeEvery(actions.DELETE_ARCHIVE.REQUEST, deleteArchive);
}

function* watchDownloadArchive() {
    yield takeEvery(actions.DOWNLOAD_ARCHIVE.REQUEST, downloadArchive);
}

function* fetchStorageStats(action) {
    try {
        const { organizationId } = action.payload;
        const result = yield call(AxiosCall, `/mycore/storage/stats/?organization=${organizationId}`);
        yield put(actions.fetchStorageStatsSuccess(result.data));
    } catch (e) {
        yield put(actions.fetchStorageStatsFailure(e.message));
    }
}

function* watchFetchStorageStats() {
    yield takeLatest(actions.FETCH_STORAGE_STATS.REQUEST, fetchStorageStats);
}

// ==================== ROOT SAGA ====================

export default function* archivesSaga() {
    yield all([
        watchFetchArchives(),
        watchFetchArchiveDetail(),
        watchUploadArchive(),
        watchUpdateArchive(),
        watchDeleteArchive(),
        watchDownloadArchive(),
        watchFetchStorageStats(),
    ]);
}
